import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { BackButton } from '@/components/ui/app/back-button';
import { PageHeader } from '@/pages/_partials/mobile-headers';
import FormProcedureEdit, { FORM_PROCEDURE_EDIT_ID } from './_partials/forms/FormProcedureEdit';
import { useProcedureTemplate } from '@/lib/hooks/use-procedure-template';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useApiCache } from '@/lib/contexts/api-cache-context';
import { ProcedureFormSkeleton } from '@/pages/procedures/index/_partials/ProcedureSkeletons';
import { backendToFrontendFieldType, frontendToBackendFieldType } from '@/lib/utils/procedure-field-types';
import { useTranslation } from 'react-i18next';

export default function ProcedureEdit() {
    const { t } = useTranslation();
    const { procedureId } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const { invalidateCache } = useApiCache();

    // Récupération du template depuis l'API
    const { template, isLoading, error, notFound } = useProcedureTemplate(procedureId);

    // Transformer le template en format procédure pour le formulaire (mémoisé)
    const procedure = useMemo(() => {
        if (!template) return null;
        const rawSections = template.configuration?.sections || [];
        return {
            id: template.uniq_id,
            uniq_id: template.uniq_id,
            name: template.name,
            description: template.description || '',
            color: template.color || '#3B82F6',
            sections: rawSections.map((section) => ({
                ...section,
                fields: (section.fields || []).map((field) => ({
                    ...field,
                    type: backendToFrontendFieldType(field.type),
                })),
            })),
        };
    }, [template]);

    console.log({ template, procedure });

    const handleSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            const payload = {
                name: data.name,
                description: data.description || null,
                color: data.color,
                configuration: {
                    sections: data.sections.map((section) => ({
                        id: section.id,
                        name: section.name || '',
                        description: section.description || '',
                        fields: section.fields.map((field) => ({
                            id: field.id,
                            type: frontendToBackendFieldType(field.type),
                            label: field.label || '',
                            required: field.required || false,
                            ...(field.options && { options: field.options.filter((opt) => opt.trim() !== '') }),
                            ...(field.instructions && { instructions: field.instructions }),
                        })),
                    })),
                },
            };

            await horodatyApi.procedureTemplates.update(procedureId, payload);
            toast.success(t('procedures:procedureMiseAJourAvecSucces'));
            invalidateCache('procedure-templates');
            // Naviguer avec un state pour forcer le refresh
            navigate(`/procedures/${procedureId}`, { state: { refresh: Date.now() } });
        } catch (err) {
            console.error('Error updating procedure:', err);
            if (err.response?.data?.code === 'ERR_PROC_0003') {
                toast.error(t('procedures:impossibleDeModifierUnTemplatePublieCreezUne'));
            } else if (err.response?.status === 422) {
                const errors = err.response.data.errors;
                const firstError = Object.values(errors || {})[0];
                toast.error(Array.isArray(firstError) ? firstError[0] : t('procedures:donneesInvalides'));
            } else if (err.response?.status === 403) {
                toast.error(t('procedures:vousNAvezPasLesDroitsPourModifier'));
            } else {
                toast.error(t('procedures:erreurLorsDeLaMiseAJourDe'));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!confirm('Êtes-vous sûr de vouloir archiver cette procédure ?')) return;

        setIsDeleting(true);
        try {
            await horodatyApi.procedureTemplates.archive(procedureId);
            toast.success(t('procedures:procedureArchiveeAvecSucces'));
            invalidateCache('procedure-templates');
            navigate('/procedures');
        } catch (err) {
            console.error('Error archiving procedure:', err);
            toast.error(t('procedures:erreurLorsDeLArchivageDeLaProcedure'));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleFormDataChange = useCallback((data) => {
        setFormData(data);
    }, []);

    // État de chargement
    if (isLoading) {
        return (
            <div className="bg-muted relative size-full overflow-hidden flex flex-col">
                <div className="flex flex-col h-full">
                    <PageHeader title={t('procedures:chargement')}>
                        <BackButton />
                    </PageHeader>
                    <ProcedureFormSkeleton />
                </div>
            </div>
        );
    }

    // État d'erreur
    if (error) {
        return (
            <div className="bg-muted relative size-full overflow-hidden flex flex-col">
                <div className="flex flex-col h-full">
                    <PageHeader title={t('procedures:erreur')}>
                        <BackButton />
                    </PageHeader>
                    <div className="text-center py-12">
                        <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                        <p className="text-muted-foreground mb-4">{t('procedures:erreurLorsDuChargementDeLaProcedure')}</p>
                        <Button variant="outline" color="neutral" onClick={() => navigate('/procedures')}>
                            {t('procedures:retourAuxProcedures')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Procédure non trouvée
    if (notFound || !procedure) {
        return (
            <div className="flex flex-col h-full items-center justify-center">
                <p className="text-muted-foreground mb-4">{t('procedures:procedureNonTrouvee')}</p>
                <Button variant="outline" color="neutral" onClick={() => navigate('/procedures')}>
                    {t('procedures:retourAuxProcedures')}
                </Button>
            </div>
        );
    }

    return (
        <div className="bg-muted relative size-full overflow-hidden flex flex-col">
            <div className="flex flex-col h-full">
                {/* Header */}
                <PageHeader title={t('procedures:editerLaProcedure')}>
                    <BackButton />
                </PageHeader>

                {/* Form Content */}
                <div className="flex-1 overflow-y-auto scrollbar-hide overscroll-contain pb-[calc(80px+64px+var(--safe-area-inset-bottom))] md:pb-[100px]">
                    <div className="md:max-w-[62.5%] md:min-w-[728px] mx-auto">
                        <FormProcedureEdit
                            procedure={procedure}
                            onSubmit={handleSubmit}
                            onFormDataChange={handleFormDataChange}
                        />
                    </div>
                </div>

                {/* Fixed Footer */}
                <div className="fixed bottom-0 left-0 right-0 bg-background px-[20px] pt-[16px] pb-[calc(16px+env(safe-area-inset-bottom,0px))] shadow-[0_-2px_10px_rgba(0,0,0,0.1)] z-30">
                    <div className="flex gap-[12px] md:max-w-[62.5%] md:min-w-[728px] mx-auto">
                        <Button
                            type="button"
                            variant="plain"
                            color="destructive"
                            size="xxl"
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="bg-muted rounded-[16px] px-[20px] py-[16px] hover:bg-destructive/15 transition-colors h-auto"
                        >
                            <Trash2 className="h-5 w-5 text-muted-foreground" />
                        </Button>
                        <SubmitButton
                            isSubmitting={isSubmitting}
                            loadingText={t('procedures:miseAJour')}
                            size="cta"
                            form={FORM_PROCEDURE_EDIT_ID}
                            className="flex-1"
                        >
                            {t('procedures:mettreAJour')}
                        </SubmitButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
